import { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, wsUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { MapPin, Phone, ShieldCheck, Star, Flag, Handshake, Check, X, Radio, CalendarClock, Lock } from "lucide-react";
import RatingStars from "@/components/RatingStars";
import { Seo } from "@/components/Seo";
import { DEFAULT_DESCRIPTION } from "@/config/site";
import { buildDossierProductJsonLd } from "@/lib/seoJsonLd";
import { getDeptChipsForDisplay, getDeptDisplayLabel } from "@/lib/departementDisplay";
import { offreVenteCorrespondARecherche } from "@/lib/rechercheCriteresMatch";
import StripePaymentDialog from "@/components/StripePaymentDialog";
import EscrowPaymentExplainer from "@/components/EscrowPaymentExplainer";
import { handleAcheterResponse, postDossierBuyParallel } from "@/lib/dossierBuyCheckout";
import BuyPaymentMethodPicker from "@/components/BuyPaymentMethodPicker";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { recoverSubscription, waitForActiveSubscription } from "@/lib/subscriptionActivation";
import SubscriptionRequiredPurchaseModal from "@/components/SubscriptionRequiredPurchaseModal";
import { isSubscriptionRequiredError } from "@/lib/subscriptionRequired";
import { startSubscriptionCheckout } from "@/lib/subscriptionCheckout";
import { hasBlockingPurchaseForDossier } from "@/lib/transactionPurchaseGuard";
import { formatPhoneForDisplay, isPhoneLikeChampName } from "@/lib/phoneFormat";
import {
  isAutreSituationProValue,
  isSituationProListField,
  isSituationProPrecisionField,
  precisionFieldNameForSituationPro,
  sortProduitChampsForDisplay,
} from "@/lib/produitChampsForm";
import { goBackOr } from "@/lib/goBackOr";
import { cn } from "@/lib/utils";
import { buyerPrivateUnlocked, normTxId } from "@/lib/buyerPrivateAccess";
import {
  dossierStatusLabelFr,
  negotiationOfferStatusLabelFr,
  negotiationStatusLabelFr,
} from "@/lib/marketplaceStatusLabels";


function normalizeKey(s) {
  return String(s || "")
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ");
}

/**
 * @param {{ embedDossierId?: string | null, onEmbedClose?: (() => void) | null }} props
 * Mode embarqué (dashboard membre) : pas de Navbar, retour via onEmbedClose.
 */
const DossierDetail = ({
  embedDossierId = null,
  onEmbedClose = null,
  embedBackLabel = "Retour à mon espace",
  embedHideNavBack = false,
  embedCompact = false,
} = {}) => {
  const { id: routeId } = useParams();
  const id = embedDossierId || routeId;
  const embedded = Boolean(embedDossierId);
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const resumeAfterAuth = useMemo(
    () => ({ from: { pathname: location.pathname, search: location.search || "" } }),
    [location.pathname, location.search],
  );
  const [dossier, setDossier] = useState(null);
  const [produit, setProduit] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [savingEdits, setSavingEdits] = useState(false);
  const [editForm, setEditForm] = useState({
    title: "",
    description: "",
    location: "",
    price: "",
    price_min: "",
    price_max: "",
    custom_fields: {},
  });
  const [negotiation, setNegotiation] = useState(null);
  const [offerAmount, setOfferAmount] = useState("");
  const [buyLoading, setBuyLoading] = useState(false);
  const [paymentDialog, setPaymentDialog] = useState({
    open: false,
    clientSecret: "",
    txId: "",
    initialPaymentMethods: null,
  });
  const [subscribeDialog, setSubscribeDialog] = useState({ open: false, plan: "monthly", dossierId: "", negId: "" });
  const [pendingBuy, setPendingBuy] = useState(null); // { dossierId, negId }
  const [subscribing, setSubscribing] = useState(false);
  const [subscriptionPay, setSubscriptionPay] = useState({ open: false, clientSecret: "", intentType: "payment", amountDue: null, currency: "eur", plan: "monthly", planAmount: null, planCurrency: "eur", nextAction: null, paymentIntentId: "" });
  /** Étapes paiement : lecture du séquestre / process avant d’appeler l’API achat (comme pour la négociation). */
  const [prePaymentExplainerOpen, setPrePaymentExplainerOpen] = useState(false);
  /** Défilement jusqu’en bas du texte explicatif avant « Continuer vers le paiement ». */
  const [prePaymentScrollDone, setPrePaymentScrollDone] = useState(false);
  const [buyPaymentMethodId, setBuyPaymentMethodId] = useState("");
  const prePaymentScrollRef = useRef(null);

  const updatePrePaymentScrollState = useCallback((el) => {
    if (!el) return;
    const { scrollTop, scrollHeight, clientHeight } = el;
    const threshold = 36;
    const fitsWithoutScroll = scrollHeight <= clientHeight + 4;
    const atBottom = fitsWithoutScroll || scrollTop + clientHeight >= scrollHeight - threshold;
    if (atBottom) setPrePaymentScrollDone(true);
  }, []);

  useEffect(() => {
    if (!prePaymentExplainerOpen) {
      setPrePaymentScrollDone(false);
      return;
    }
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      updatePrePaymentScrollState(prePaymentScrollRef.current);
    };
    run();
    const id = requestAnimationFrame(run);
    const t = window.setTimeout(run, 120);
    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
      window.clearTimeout(t);
    };
  }, [prePaymentExplainerOpen, updatePrePaymentScrollState]);
  const [subscriptionSyncDialog, setSubscriptionSyncDialog] = useState({
    open: false,
    plan: "monthly",
    paymentIntentId: "",
    syncing: false,
  });
  const [callActive, setCallActive] = useState(false);
  const [myDossiers, setMyDossiers] = useState([]);
  const [proposeRdvOpen, setProposeRdvOpen] = useState(false);
  /** IDs des fiches vente cochées pour une proposition multi vers une annonce recherche */
  const [proposeVenteSelected, setProposeVenteSelected] = useState(() => new Set());
  const [proposeSubmitting, setProposeSubmitting] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [myBuyerTransactions, setMyBuyerTransactions] = useState([]);
  /** false tant que le premier chargement des transactions acheteur n’est pas terminé (évite bouton « Acheter » cliquable trop tôt). */
  const [buyerTxsReady, setBuyerTxsReady] = useState(false);
  const wsRef = useRef(null);

  const isSeller = useMemo(() => {
    if (!user?.user_id || !dossier?.seller_id) return false;
    return String(user.user_id) === String(dossier.seller_id);
  }, [user?.user_id, dossier?.seller_id]);

  const isAdminViewer = user?.role === "admin";

  const purchaseBlockedOnDossier = useMemo(
    () => hasBlockingPurchaseForDossier(myBuyerTransactions, id),
    [myBuyerTransactions, id],
  );

  const canSeePrivate = isSeller || unlocked || isAdminViewer;

  const openEdit = useCallback(() => {
    if (!dossier) return;
    setEditForm({
      title: dossier.title || "",
      description: dossier.description || "",
      location: dossier.location || "",
      price: dossier.price != null ? String(dossier.price) : "",
      price_min: dossier.price_min != null ? String(dossier.price_min) : "",
      price_max: dossier.price_max != null ? String(dossier.price_max) : "",
      custom_fields:
        dossier.custom_fields && typeof dossier.custom_fields === "object" && !Array.isArray(dossier.custom_fields)
          ? { ...dossier.custom_fields }
          : {},
    });
    setEditMode(true);
  }, [dossier]);

  const cancelEdit = useCallback(() => {
    setEditMode(false);
    setSavingEdits(false);
  }, []);

  const saveEdits = useCallback(async () => {
    if (!dossier?.dossier_id) return;
    const asNullableNumber = (v) => {
      if (v == null || v === "") return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    };
    const payload = {
      title: String(editForm.title || "").trim(),
      description: String(editForm.description || "").trim(),
      location: String(editForm.location || "").trim(),
      custom_fields:
        editForm.custom_fields && typeof editForm.custom_fields === "object" && !Array.isArray(editForm.custom_fields)
          ? editForm.custom_fields
          : {},
    };
    const typeDossier = String(dossier.type || "").toLowerCase().trim() || "vente";
    if (typeDossier === "recherche") {
      payload.price_min = asNullableNumber(editForm.price_min);
      payload.price_max = asNullableNumber(editForm.price_max);
      payload.price = payload.price_max ?? null;
    } else {
      payload.price = asNullableNumber(editForm.price);
      payload.price_min = null;
      payload.price_max = null;
    }
    setSavingEdits(true);
    try {
      const { data } = await api.put(`/dossiers/${dossier.dossier_id}`, payload);
      if (data) setDossier(data);
      toast.success("Annonce modifiée");
      setEditMode(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Modification impossible");
    } finally {
      setSavingEdits(false);
    }
  }, [dossier, editForm]);

  const load = useCallback(async () => {
    let row;
    try {
      if (user?.role === "admin") {
        const d = await api.get("/admin/dossier", { params: { id } });
        const payload = d?.data;
        row =
          payload && typeof payload === "object" && payload.data != null && typeof payload.data === "object"
            ? payload.data
            : payload;
      } else {
        const d = await api.get(`/dossiers/${id}`);
        row = d.data;
      }
      setDossier(row);
    } catch (e) {
      const st = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message;
      if (st === 404 || detail === "Dossier introuvable") {
        toast.error(
          "Impossible d’ouvrir cette annonce depuis ce lien (retirée du catalogue ou accès restreint). Si vous avez un achat en cours, utilisez Tableau de bord → Mes achats.",
          { duration: 8000 },
        );
      } else {
        toast.error(typeof detail === "string" && detail ? detail : "Impossible de charger l’annonce.");
      }
      navigate("/marketplace");
      return;
    }
    try {
      const st = await api.get(`/calls/status/${id}`);
      setCallActive(Boolean(st?.data?.call_active));
    } catch {
      setCallActive(false);
    }
    try {
      const pList = await api.get("/produits");
      const p = pList.data.find((x) => x.produit_id === row.produit_id);
      setProduit(p);
    } catch {
      setProduit(null);
    }
  }, [id, navigate, user?.role]);

  const loadNegotiation = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get(`/dossiers/${id}/negotiations/mine`);
      setNegotiation(data);
    } catch {}
  }, [id, user]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadNegotiation();
  }, [loadNegotiation]);

  useEffect(() => {
    let cancelled = false;
    if (!user?.user_id || !id) {
      setMyBuyerTransactions([]);
      setBuyerTxsReady(true);
      return undefined;
    }
    setBuyerTxsReady(false);
    api
      .get("/transactions/mine", { params: { as_role: "buyer" } })
      .then((res) => {
        if (cancelled) return;
        setMyBuyerTransactions(Array.isArray(res?.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setMyBuyerTransactions([]);
      })
      .finally(() => {
        if (!cancelled) setBuyerTxsReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.user_id, id]);

  useEffect(() => {
    let alive = true;
    // Démasquage acheteur : voir `buyerPrivateAccess` (litige ouvert → masqué ; refund clos → masqué ; fonds_debloques ou release clos → visible).
    if (!user?.user_id || !id) {
      setUnlocked(false);
      return undefined;
    }
    Promise.all([api.get("/transactions/mine", { params: { as_role: "buyer" } }), api.get("/disputes/mine")])
      .then(([resTx, resDisp]) => {
        if (!alive) return;
        const txs = Array.isArray(resTx?.data) ? resTx.data : [];
        const disputes = Array.isArray(resDisp?.data) ? resDisp.data : [];
        const txForDossier = txs.find((t) => String(t?.dossier_id || "") === String(id));
        if (!txForDossier) {
          setUnlocked(false);
          return;
        }
        const tid = normTxId(txForDossier.transaction_id ?? txForDossier.id);
        const litigesForTx = disputes.filter((d) => normTxId(d?.transaction_id) === tid);
        setUnlocked(buyerPrivateUnlocked(txForDossier, litigesForTx));
      })
      .catch(() => {
        if (!alive) return;
        setUnlocked(false);
      });
    return () => {
      alive = false;
    };
  }, [user?.user_id, id]);

  useEffect(() => {
    if (!user?.user_id || !dossier) {
      return undefined;
    }
    if (dossier.seller_id === user.user_id) {
      return undefined;
    }
    if (dossier.type !== "recherche") {
      setMyDossiers([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/dossiers/mine");
        if (!cancelled) {
          setMyDossiers(Array.isArray(data) ? data : []);
        }
      } catch {
        if (!cancelled) {
          setMyDossiers([]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, dossier]);

  useEffect(() => {
    let cancelled = false;
    const ws = new WebSocket(wsUrl(`/api/ws/dossiers/${id}`));
    wsRef.current = ws;
    ws.onmessage = (ev) => {
      if (cancelled) return;
      try {
        const data = JSON.parse(ev.data);
        if (data.type === "call_started") setCallActive(true);
        if (data.type === "call_ended") setCallActive(false);
        if (data.type === "dossier_sold") load();
      } catch {}
    };
    ws.onerror = () => {}; // silence
    return () => {
      cancelled = true;
      if (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING) {
        try { ws.close(); } catch {}
      }
    };
  }, [id, load]);

  const ventesEligibles = useMemo(() => {
    if (!dossier || dossier.type !== "recherche") {
      return [];
    }
    return (myDossiers || []).filter(
      (d) =>
        d &&
        d.type !== "recherche" &&
        d.status === "online" &&
        String(d.produit_id) === String(dossier.produit_id) &&
        offreVenteCorrespondARecherche(dossier, d)
    );
  }, [dossier, myDossiers]);

  const startNegotiation = async () => {
    if (!user) return navigate("/register", { state: resumeAfterAuth });
    if (dossier.type === "recherche") return;
    try {
      const { data } = await api.post(`/dossiers/${id}/negotiations`, {});
      setNegotiation({ ...data, offers: [] });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible de démarrer la négociation");
    }
  };

  const toggleProposeVente = (dossierId) => {
    const key = String(dossierId);
    setProposeVenteSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const sendRechercheProposals = async () => {
    if (!user) return navigate("/register", { state: resumeAfterAuth });
    const vente_dossier_ids = Array.from(proposeVenteSelected);
    if (!vente_dossier_ids.length) {
      toast.error("Cochez au moins une fiche de vente éligible.");
      return;
    }
    setProposeSubmitting(true);
    try {
      const { data } = await api.post("/recherche-proposals/batch", {
        recherche_dossier_id: id,
        vente_dossier_ids,
      });
      const created = Array.isArray(data?.created_ids) ? data.created_ids.length : 0;
      const skipped = Array.isArray(data?.skipped_already_active) ? data.skipped_already_active.length : 0;
      if (created > 0) {
        toast.success(
          created === 1 ? "Proposition envoyée à l’acheteur." : `${created} propositions envoyées à l’acheteur.`,
        );
      }
      if (skipped > 0) {
        toast.info(`${skipped} fiche(s) avaient déjà une proposition en cours ou acceptée — ignorée(s).`);
      }
      setProposeRdvOpen(false);
      setProposeVenteSelected(new Set());
    } catch (e) {
      toast.error(e.response?.data?.detail || "Impossible d’envoyer les propositions");
    } finally {
      setProposeSubmitting(false);
    }
  };

  const sendOffer = async () => {
    const amount = parseFloat(offerAmount);
    if (!amount || amount <= 0) return toast.error("Montant invalide");
    const optimisticOffer = {
      offer_id: `tmp_${Date.now()}`,
      from_user_id: user?.user_id,
      amount,
      status: "sent",
      created_at: new Date().toISOString(),
    };
    try {
      await api.post(`/negotiations/${negotiation.negotiation_id}/offers`, { amount });
      setOfferAmount("");
      setNegotiation((prev) => {
        if (!prev) return prev;
        const offers = Array.isArray(prev.offers) ? prev.offers : [];
        // Évite les doublons si le reload serveur arrive très vite.
        const already = offers.some(
          (o) => Number(o?.amount) === amount && o?.from_user_id === optimisticOffer.from_user_id
        );
        return already ? prev : { ...prev, offers: [...offers, optimisticOffer] };
      });
      loadNegotiation();
      toast.success("Offre envoyée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const respond = async (action, counter) => {
    try {
      const params = { action };
      if (action === "counter") params.counter_amount = parseFloat(counter);
      await api.post(`/negotiations/${negotiation.negotiation_id}/respond`, null, { params });
      loadNegotiation();
      toast.success(action === "accept" ? "Offre acceptée" : action === "reject" ? "Offre refusée" : "Contre-offre envoyée");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const attemptBuy = useCallback(
    async ({ dossierId, negId, paymentMethodId }) => {
      const params = {
        ...(negId ? { negotiation_id: negId } : {}),
        ...(paymentMethodId ? { payment_method_id: String(paymentMethodId) } : {}),
      };
      const { data, initialPaymentMethods } = await postDossierBuyParallel(dossierId, params);
      const outcome = await handleAcheterResponse(data, {
        toast,
        initialPaymentMethods,
        onOpenPaymentDialog: ({ clientSecret, transactionId, initialPaymentMethods: pm }) => {
          setPaymentDialog({
            open: true,
            clientSecret,
            txId: transactionId,
            initialPaymentMethods: pm ?? initialPaymentMethods,
          });
        },
      });
      if (outcome === "done") {
        await load();
        await loadNegotiation();
        navigate("/dashboard?tab=buys");
        toast.success("Achat enregistré. Onglet « Mes achats » : suivez le RDV et lancez l’appel.");
        return true;
      }
      if (outcome === "modal") return true;
      throw new Error("Paiement indisponible");
    },
    [load, loadNegotiation, navigate]
  );

  const buy = useCallback(async () => {
    if (!user) return navigate("/register", { state: resumeAfterAuth });
    setBuyLoading(true);
    const neg_id = negotiation?.status === "accepted" ? negotiation.negotiation_id : null;
    try {
      await attemptBuy({
        dossierId: String(id),
        negId: neg_id ? String(neg_id) : "",
        paymentMethodId: buyPaymentMethodId || undefined,
      });
    } catch (e) {
      if (isSubscriptionRequiredError(e)) {
        const dossierId = String(id);
        const negId = String(neg_id || "");
        setPendingBuy({ dossierId, negId });
        setSubscribeDialog({ open: true, plan: "monthly", dossierId, negId });
      } else if (e.response?.status === 409) {
        toast.error(e?.response?.data?.detail || "Vous avez déjà une commande pour cette annonce.");
        try {
          const res = await api.get("/transactions/mine", { params: { as_role: "buyer" } });
          setMyBuyerTransactions(Array.isArray(res?.data) ? res.data : []);
        } catch {
          // ignore
        }
      } else {
        toast.error(e?.response?.data?.detail || e?.message || "Erreur paiement");
      }
    } finally {
      setBuyLoading(false);
    }
  }, [
    attemptBuy,
    buyPaymentMethodId,
    id,
    navigate,
    negotiation?.negotiation_id,
    negotiation?.status,
    resumeAfterAuth,
    user,
  ]);

  const continueAfterPaymentExplainer = useCallback(() => {
    setPrePaymentExplainerOpen(false);
    window.setTimeout(() => void buy(), 0);
  }, [buy]);

  const startSubscribe = async (plan) => {
    const dossierId = subscribeDialog?.dossierId || pendingBuy?.dossierId || String(id);
    const negId = subscribeDialog?.negId ?? pendingBuy?.negId ?? "";
    setSubscribing(true);
    try {
      if (dossierId) setPendingBuy({ dossierId: String(dossierId), negId: String(negId || "") });
      await startSubscriptionCheckout(api, {
        plan,
        resumeDossierId: dossierId,
        resumeNegId: negId,
      });
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Impossible d’ouvrir Stripe");
      setSubscribing(false);
    }
  };

  const resumePendingBuy = useCallback(async () => {
    const p = pendingBuy;
    if (!p?.dossierId) return;
    // Retente quelques fois : l’abonnement peut mettre quelques secondes à devenir "actif" côté API.
    for (let i = 0; i < 5; i += 1) {
      try {
        await attemptBuy({ dossierId: p.dossierId, negId: p.negId });
        setPendingBuy(null);
        return;
      } catch (e) {
        if (isSubscriptionRequiredError(e)) {
          await new Promise((r) => window.setTimeout(r, 1200));
          continue;
        }
        toast.error(e?.response?.data?.detail || e?.message || "Erreur paiement");
        setPendingBuy(null);
        return;
      }
    }
    toast.info("Abonnement en cours d’activation… réessaie dans quelques secondes.");
  }, [attemptBuy, pendingBuy]);

  const handleSubscriptionSyncRetry = useCallback(async () => {
    const { plan, paymentIntentId } = subscriptionSyncDialog;
    if (!paymentIntentId) return;
    setSubscriptionSyncDialog((prev) => ({ ...prev, syncing: true }));
    try {
      await recoverSubscription(api, { plan, paymentIntentId });
      const ok = await waitForActiveSubscription(api);
      if (ok) {
        setSubscriptionSyncDialog({ open: false, plan: "monthly", paymentIntentId: "", syncing: false });
        toast.success("Abonnement actif");
        await resumePendingBuy();
      } else {
        toast.error("Encore en attente — réessayez dans quelques secondes.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Synchronisation impossible");
    } finally {
      setSubscriptionSyncDialog((prev) => ({ ...prev, syncing: false }));
    }
  }, [subscriptionSyncDialog, resumePendingBuy]);

  useEffect(() => {
    try {
      const sp = new URLSearchParams(window.location.search || "");
      if (sp.get("checkout_success") !== "1" && !sp.get("session_id")) return;
      const dossierId = sp.get("resume_dossier_id");
      const negId = sp.get("resume_negotiation_id") || "";
      sp.delete("checkout_success");
      sp.delete("session_id");
      sp.delete("resume_dossier_id");
      sp.delete("resume_negotiation_id");
      sp.delete("checkout_cancel");
      const next = `${window.location.pathname}${sp.toString() ? `?${sp}` : ""}${window.location.hash || ""}`;
      window.history.replaceState({}, "", next);
      if (!dossierId) return;

      setPendingBuy({ dossierId: String(dossierId), negId: String(negId) });
      (async () => {
        const active = await waitForActiveSubscription(api);
        if (!active) {
          toast.info("Finalisation de l’abonnement… réessayez dans quelques secondes.");
          return;
        }
        toast.success("Abonnement actif");
        try {
          await attemptBuy({ dossierId: String(dossierId), negId: String(negId) });
          setPendingBuy(null);
        } catch (e) {
          if (isSubscriptionRequiredError(e)) {
            setSubscribeDialog({ open: true, plan: "monthly", dossierId: String(dossierId), negId: String(negId) });
          } else {
            toast.error(e?.response?.data?.detail || e?.message || "Erreur paiement");
          }
        }
      })();
    } catch {
      // ignore
    }
  }, [attemptBuy]);

  const productJsonLd = useMemo(
    () => (dossier && id ? buildDossierProductJsonLd(dossier, id) : null),
    [dossier, id],
  );

  if (!dossier) {
    const loadingShell = (
      <div
        className={cn(
          "flex items-center justify-center text-slate-500",
          embedded ? "min-h-[12rem] py-8" : "min-h-screen",
        )}
      >
        Chargement…
      </div>
    );
    if (embedded) return loadingShell;
    return (
      <div className="min-h-screen bg-white">
        <Seo
          title="Fiche annonce"
          description={DEFAULT_DESCRIPTION}
          canonicalPath={`/dossier/${id}`}
          ogType="product"
        />
        <Navbar />
        {loadingShell}
      </div>
    );
  }

  const isRecherche = dossier.type === "recherche";
  const deptLabel = getDeptDisplayLabel(dossier);
  const deptChips = getDeptChipsForDisplay(dossier);
  const locationTrim = String(dossier?.location || "").trim();
  const deptLabelTrim = deptLabel ? String(deptLabel).trim() : "";
  const showDeptChips = Boolean(deptLabelTrim) && !editMode;
  const showLocationPin =
    canSeePrivate && locationTrim && !isRecherche && !editMode && !(deptLabelTrim && locationTrim === deptLabelTrim);
  const compactEmbed = Boolean(embedded && embedCompact);
  const rechercheBudgetLabel =
    isRecherche && dossier.price_min != null && dossier.price_max != null
      ? `${Number(dossier.price_min).toLocaleString("fr-FR")} – ${Number(dossier.price_max).toLocaleString("fr-FR")} €`
      : null;
  const lastOffer = negotiation?.offers?.[negotiation.offers.length - 1];
  const canRespondToLast = negotiation?.status === "open" && lastOffer && lastOffer.from_user_id !== user?.user_id;
  const publicPrice = dossier.price;
  const negotiatedFinalPrice = negotiation?.final_price || null;
  const buyPrice = negotiation?.status === "accepted" && negotiatedFinalPrice != null ? negotiatedFinalPrice : publicPrice;
  const dossierReturnUrl = `${window.location.origin}/dossier/${id}`;

  return (
    <div className={embedded ? "min-h-0 bg-white" : "min-h-screen bg-white"}>
      {!embedded ? (
        <Seo
          title={dossier.title}
          description={(dossier.description || dossier.title || "").slice(0, 158)}
          canonicalPath={`/dossier/${id}`}
          ogType="product"
          imageAlt={dossier.title}
          jsonLd={productJsonLd}
        />
      ) : null}
      {!embedded ? <Navbar /> : null}
      <div
        className={cn(
          "mx-auto min-w-0",
          compactEmbed ? "max-w-none py-0" : "max-w-6xl px-4 sm:px-6",
          !compactEmbed && (embedded ? "py-4 sm:py-5" : "py-8 sm:py-12"),
        )}
      >
        {!(embedded && embedHideNavBack) ? (
          <div className="mb-6">
            <button
              type="button"
              className="text-sm text-slate-500 hover:text-[#2D7FF9]"
              data-testid={embedded ? "dash-dossier-back" : "back-previous"}
              onClick={() => (embedded ? onEmbedClose() : goBackOr(navigate))}
            >
              ← {embedded ? embedBackLabel : "Retour"}
            </button>
          </div>
        ) : null}

        <div className={cn("grid min-w-0", compactEmbed ? "grid-cols-1 gap-3" : "gap-8 lg:grid-cols-3 lg:gap-10")}>
          <div className={cn("min-w-0", compactEmbed ? "order-2" : "lg:col-span-2")}>
            <div className={cn("flex items-center gap-2 flex-wrap", compactEmbed ? "mb-2" : "mb-3")}>
              <Badge className="bg-white text-[#2D7FF9] border border-[#2D7FF9]/20 hover:bg-white">{dossier.secteur}</Badge>
              {dossier.type === "recherche" && <Badge className="bg-[#0B0D12] text-white hover:bg-[#0B0D12]">Recherche</Badge>}
              {dossier.hors_saas && (
                <Badge variant="outline" className="text-amber-800 border-amber-300 bg-amber-50">
                  Hors marketplace (SaaS)
                </Badge>
              )}
              {callActive && (
                <Badge className="bg-emerald-500 text-white hover:bg-emerald-500 flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-white pulse-ring"></span>
                  Client occupé par un acheteur
                </Badge>
              )}
            </div>
            <div
              className={cn(
                "flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500",
                compactEmbed ? "mb-3" : "mb-5",
              )}
              data-testid="dossier-annonce-meta"
            >
              {dossier.annonce_numero && (
                <span>
                  <span className="text-slate-400">Réf. </span>
                  <span className="font-mono font-semibold text-slate-700 tabular-nums">{dossier.annonce_numero}</span>
                </span>
              )}
              {dossier.created_at && (
                <span>
                  <span className="text-slate-400">Publié le </span>
                  {new Date(dossier.created_at).toLocaleString("fr-FR", {
                    dateStyle: "medium",
                    timeStyle: "short",
                  })}
                </span>
              )}
            </div>

            {isAdminViewer && dossier.seller_id ? (
              <div
                className={cn(
                  "rounded-lg border border-violet-200/90 bg-violet-50/90 px-3 py-2.5 text-sm",
                  compactEmbed ? "mb-3" : "mb-5",
                )}
                data-testid="admin-dossier-poster"
              >
                <p className="text-[10px] font-semibold uppercase tracking-wide text-violet-900">
                  Annonceur (admin)
                </p>
                <dl className="mt-2 space-y-1 text-slate-800">
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                    <dt className="text-slate-500 shrink-0">Nom</dt>
                    <dd className="font-medium">{dossier.seller_name || "—"}</dd>
                  </div>
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                    <dt className="text-slate-500 shrink-0">Email</dt>
                    <dd>
                      {dossier.seller_email ? (
                        <a
                          href={`mailto:${dossier.seller_email}`}
                          className="font-medium text-[#2D7FF9] hover:underline break-all"
                        >
                          {dossier.seller_email}
                        </a>
                      ) : (
                        "—"
                      )}
                    </dd>
                  </div>
                  {dossier.seller_phone ? (
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                      <dt className="text-slate-500 shrink-0">Tél.</dt>
                      <dd className="font-medium tabular-nums">{dossier.seller_phone}</dd>
                    </div>
                  ) : null}
                  {dossier.seller_company ? (
                    <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                      <dt className="text-slate-500 shrink-0">Société</dt>
                      <dd>{dossier.seller_company}</dd>
                    </div>
                  ) : null}
                  <div className="flex flex-wrap gap-x-2 gap-y-0.5">
                    <dt className="text-slate-500 shrink-0">ID</dt>
                    <dd className="font-mono text-xs text-slate-600 break-all">{dossier.seller_id}</dd>
                  </div>
                </dl>
              </div>
            ) : null}

            <h1
              className={cn(
                "mb-3 break-words font-display font-black leading-tight tracking-[-0.03em]",
                compactEmbed ? "text-xl sm:text-2xl" : "mb-4 text-2xl sm:text-3xl md:text-4xl lg:text-5xl",
              )}
              data-testid="dossier-title"
            >
              {editMode ? (
                <Input
                  value={editForm.title}
                  onChange={(e) => setEditForm((p) => ({ ...p, title: e.target.value }))}
                  className="h-12 text-base sm:text-lg font-display font-black"
                  data-testid="edit-title"
                />
              ) : (
                dossier.title
              )}
            </h1>
            <div className={cn("flex flex-wrap items-center gap-2 text-sm text-slate-600", compactEmbed ? "mb-4" : "mb-8 gap-5")}>
              {showDeptChips ? (
                <div className="flex flex-wrap items-center gap-1.5" data-testid="dossier-dept-chips">
                  <MapPin className="h-4 w-4 shrink-0 text-[#2D7FF9]" aria-hidden />
                  {deptChips.map((code) => (
                    <Badge
                      key={code}
                      variant="secondary"
                      className="font-mono text-xs tabular-nums bg-sky-50 text-sky-900 border-sky-200/80"
                    >
                      Dép. {code}
                    </Badge>
                  ))}
                </div>
              ) : null}
              {showLocationPin ? (
                <div className="flex items-center gap-1.5 text-slate-600">
                  <MapPin className="h-4 w-4 text-slate-400" aria-hidden />
                  <span>{dossier.location}</span>
                </div>
              ) : null}
              {dossier.seller_name && !isAdminViewer ? (
                <Link to={`/profil/${dossier.seller_id}`} className="flex items-center gap-1.5 hover:text-[#2D7FF9]">
                  {(dossier.seller_kyc_status === "approved") && (
                    <span className="inline-flex items-center justify-center w-4 h-4 rounded-full bg-emerald-500" title="Encaissement Stripe vérifié (vendeur)" data-testid="detail-verified-badge">
                      <ShieldCheck className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </span>
                  )}
                  {(dossier.seller_rating_count || 0) > 0 ? (
                    <>
                      <Star className="w-4 h-4 fill-[#FBBF24] text-[#FBBF24]" />
                      <span className="tabular-nums font-semibold">{(dossier.seller_rating || 0).toFixed(1)}</span>
                      <span className="text-slate-400">({dossier.seller_rating_count} avis)</span>
                    </>
                  ) : (
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-400">Nouveau vendeur</span>
                  )}
                  <span>· {dossier.seller_name}</span>
                </Link>
              ) : null}
            </div>

            <div className={cn("max-w-none", compactEmbed ? "mb-4" : "mb-8 prose")}>
              <h3 className={cn("font-display font-bold text-slate-900", compactEmbed ? "text-base mb-2" : "text-xl mb-3")}>
                Description
              </h3>
              {editMode ? (
                <Textarea
                  value={editForm.description}
                  onChange={(e) => setEditForm((p) => ({ ...p, description: e.target.value }))}
                  rows={6}
                  data-testid="edit-description"
                />
              ) : (
                <p className="text-slate-700 whitespace-pre-line">{dossier.description}</p>
              )}
            </div>

            {produit && produit.champs?.length > 0 && (() => {
              const typeDossier = dossier.type || "vente";
              const cibleAnnonce = (c) =>
                c.cible_annonces == null || c.cible_annonces === "tous" || c.cible_annonces === typeDossier;
              const isDeptChamp = (c) => {
                const nk = normalizeKey(c?.nom);
                return nk === "departement" || nk === "département" || nk.includes("departement");
              };
              // En lecture seule on affiche déjà le département en haut : éviter doublon dans la grille "Qualification".
              const hideDeptInQualif = !editMode && Boolean(deptLabelTrim);
              const champs = sortProduitChampsForDisplay(
                (produit.champs || [])
                  .filter(cibleAnnonce)
                  .filter((c) => !(hideDeptInQualif && isDeptChamp(c)))
                  .filter((c) => !isSituationProPrecisionField(c.nom)),
                { produitNom: produit.nom },
              );
              const displayChampVal = (nom, raw) => {
                if (raw == null || raw === "") return "—";
                const s = String(raw);
                if (isSituationProListField(nom) && isAutreSituationProValue(s)) {
                  const precNom = precisionFieldNameForSituationPro(nom);
                  const prec = precNom ? dossier.custom_fields?.[precNom] : null;
                  if (prec != null && String(prec).trim() !== "") {
                    return `Autre — ${String(prec).trim()}`;
                  }
                }
                return isPhoneLikeChampName(nom) ? formatPhoneForDisplay(s) : s;
              };
              const champHasValue = (nom) => {
                const raw = dossier.custom_fields?.[nom];
                if (raw == null || raw === "") return false;
                if (typeof raw === "boolean") return true;
                return String(raw).trim() !== "";
              };
              let visCatalogue = champs.filter((c) => c.visible_public !== false);
              if (!editMode && (compactEmbed || isRecherche)) {
                visCatalogue = visCatalogue.filter((c) => champHasValue(c.nom));
              }
              // Annonce "recherche" : pas de notion de coordonnées client masquées / déblocage après RDV.
              const champsPrives =
                !isRecherche && canSeePrivate ? champs.filter((c) => c.visible_public === false) : [];
              if (!canSeePrivate && !visCatalogue.length) return null;
              if (canSeePrivate && !visCatalogue.length && !champsPrives.length) return null;
              return (
                <div
                  className={cn(
                    "mb-6 rounded-xl border border-slate-200/90 bg-slate-50/50",
                    compactEmbed ? "p-4" : "mb-8 p-6",
                  )}
                  data-testid="dossier-champs-qualif"
                >
                  <h3 className={cn("font-display font-bold text-slate-900", compactEmbed ? "text-base mb-3" : "text-lg mb-4")}>
                    Qualification
                  </h3>
                  {visCatalogue.length > 0 && (
                    <>
                      {!compactEmbed ? (
                        <p className="text-xs text-slate-500 mb-3">Critères visibles sur la marketplace.</p>
                      ) : null}
                      <dl className={cn("grid gap-3", compactEmbed ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2 gap-4")}>
                        {visCatalogue.map((c) => (
                          <div
                            key={c.champ_id ?? c.nom}
                            className={cn(
                              "flex gap-3 rounded-lg bg-white px-3 py-2.5 border border-slate-100",
                              compactEmbed ? "flex-col sm:flex-row sm:items-center sm:justify-between" : "justify-between border-b border-slate-100 pb-2 rounded-none px-0 py-0 bg-transparent border-0 border-b",
                            )}
                          >
                            <dt className="text-sm font-medium text-slate-600">{c.nom}</dt>
                            <dd className={cn("text-sm font-semibold text-slate-900 tabular-nums", compactEmbed ? "" : "text-right pl-2")}>
                              {editMode ? (
                                c.type === "boolean" ? (
                                  <div className="flex items-center justify-end">
                                    <Checkbox
                                      checked={Boolean(editForm.custom_fields?.[c.nom])}
                                      onCheckedChange={(v) =>
                                        setEditForm((p) => ({
                                          ...p,
                                          custom_fields: { ...(p.custom_fields || {}), [c.nom]: Boolean(v) },
                                        }))
                                      }
                                    />
                                  </div>
                                ) : (
                                  <Input
                                    value={editForm.custom_fields?.[c.nom] ?? ""}
                                    onChange={(e) =>
                                      setEditForm((p) => ({
                                        ...p,
                                        custom_fields: { ...(p.custom_fields || {}), [c.nom]: e.target.value },
                                      }))
                                    }
                                    className="h-9 text-right"
                                    data-testid={`edit-cf-${c.nom}`}
                                  />
                                )
                              ) : (
                                displayChampVal(c.nom, dossier.custom_fields?.[c.nom])
                              )}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </>
                  )}
                  {champsPrives.length > 0 && (
                    <div className={visCatalogue.length > 0 ? "mt-6 border-t border-amber-200 pt-4" : ""}>
                      <h4 className="text-sm font-semibold text-amber-900 mb-2">
                        {isAdminViewer
                          ? "Champs masqués sur la marketplace (confidentiels prospect)"
                          : "Champs confidentiels prospect — visibles après validation du RDV, ou après clôture de litige si la décision autorise l’accès (masqués pendant un litige en cours ou après remboursement suite à litige)."}
                      </h4>
                      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {champsPrives.map((c) => (
                          <div key={c.champ_id ?? c.nom} className="flex justify-between border-b border-amber-100 pb-2">
                            <dt className="text-sm text-amber-800/80">{c.nom}</dt>
                            <dd className="text-sm font-medium text-right pl-2 tabular-nums">
                              {editMode ? (
                                c.type === "boolean" ? (
                                  <div className="flex items-center justify-end">
                                    <Checkbox
                                      checked={Boolean(editForm.custom_fields?.[c.nom])}
                                      onCheckedChange={(v) =>
                                        setEditForm((p) => ({
                                          ...p,
                                          custom_fields: { ...(p.custom_fields || {}), [c.nom]: Boolean(v) },
                                        }))
                                      }
                                    />
                                  </div>
                                ) : (
                                  <Input
                                    value={editForm.custom_fields?.[c.nom] ?? ""}
                                    onChange={(e) =>
                                      setEditForm((p) => ({
                                        ...p,
                                        custom_fields: { ...(p.custom_fields || {}), [c.nom]: e.target.value },
                                      }))
                                    }
                                    className="h-9 text-right"
                                    data-testid={`edit-cf-private-${c.nom}`}
                                  />
                                )
                              ) : (
                                displayChampVal(c.nom, dossier.custom_fields?.[c.nom])
                              )}
                            </dd>
                          </div>
                        ))}
                      </dl>
                    </div>
                  )}
                </div>
              );
            })()}

            {!compactEmbed && !isRecherche ? (
            <div className="bg-[#F9FAFB] border border-[#E5E7EB] rounded-lg p-6 flex items-start gap-4">
              <ShieldCheck className="w-8 h-8 text-[#2D7FF9] flex-shrink-0" strokeWidth={1.5} />
              <div className="text-sm text-slate-700">
                <strong className="font-display">Numéro client jamais visible.</strong> Tous les appels passent par notre proxy vocal sécurisé,
                les deux numéros restent totalement anonymes. Toute tentative de détournement hors plateforme est
                détectée par IA et bloquée.
              </div>
            </div>
            ) : null}
          </div>

          {/* RIGHT PANEL: en modale compacte, affiché en premier (prix + actions visibles sans scroll). */}
          <aside className={cn("min-w-0 self-start", compactEmbed ? "order-1" : "")}>
            <div
              className={cn(
                "rounded-xl border border-slate-200/90 bg-white shadow-sm",
                compactEmbed ? "p-4 mb-2" : "p-6 mb-4",
              )}
            >
              <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                {isRecherche ? "Budget / prix cible" : "Prix du dossier"}
              </div>
              <div
                className={cn(
                  "font-display font-black text-[#2D7FF9] tabular-nums",
                  compactEmbed ? "text-3xl mb-0" : "text-5xl mb-1",
                )}
                data-testid="dossier-price"
              >
                {editMode ? (
                  isRecherche ? (
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={editForm.price_min}
                        onChange={(e) => setEditForm((p) => ({ ...p, price_min: e.target.value }))}
                        className="h-11 text-lg font-mono tabular-nums"
                        placeholder="Min"
                        data-testid="edit-price-min"
                      />
                      <Input
                        type="text"
                        inputMode="decimal"
                        value={editForm.price_max}
                        onChange={(e) => setEditForm((p) => ({ ...p, price_max: e.target.value }))}
                        className="h-11 text-lg font-mono tabular-nums"
                        placeholder="Max"
                        data-testid="edit-price-max"
                      />
                    </div>
                  ) : (
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={editForm.price}
                      onChange={(e) => setEditForm((p) => ({ ...p, price: e.target.value }))}
                      className="h-11 text-lg font-mono tabular-nums"
                      data-testid="edit-price"
                    />
                  )
                ) : isRecherche && rechercheBudgetLabel ? (
                  rechercheBudgetLabel
                ) : (
                  `${Number(publicPrice).toLocaleString("fr-FR")}€`
                )}
              </div>
              {!isRecherche && (
                <div className="text-xs text-slate-500 mb-6">Commission vendeur 10% · acheteur : aucun frais supplémentaire</div>
              )}
              {isRecherche && (
                <p className={cn("text-xs text-slate-600 leading-relaxed", compactEmbed ? "mt-3 mb-3" : "mb-6")}>
                  {compactEmbed && isSeller
                    ? "Votre demande d’achat — les vendeurs proposent si leur fiche correspond au produit et aux critères."
                    : "Demande d’achat — le produit doit correspondre."}{" "}
                  {Array.isArray(dossier.recherche_criteres_requis) && dossier.recherche_criteres_requis.length > 0 ? (
                    <>
                      <strong>Alignement requis</strong> sur <strong>{dossier.recherche_criteres_requis.join(" · ")}</strong> : la
                      même réponse sur l’annonce d’achat et sur l’annonce de vente, pas d’autre texte. Trop d’écart, ce
                      n’est plus la bonne annonce à laquelle répondre.
                    </>
                  ) : Array.isArray(dossier.recherche_criteres_requis) && dossier.recherche_criteres_requis.length === 0 ? (
                    "Aucun champ n’exige l’égalité stricte en plus du produit : c’est l’adéquation classique, comme d’habitude."
                  ) : (
                    "Aucun critère strict n’est enregistré sur cette fiche. Appliquez l’adéquation annonce d’achat / annonce de vente, comme d’ordinaire."
                  )}{" "}
                  Ensuite : négociation, RDV, etc.
                </p>
              )}

              {dossier.status !== "online" && (
                <Badge className="bg-slate-200 text-slate-700 mb-4">Statut : {dossierStatusLabelFr(dossier.status)}</Badge>
              )}

              {!isSeller && dossier.status === "online" && (
                <>
                  {!isRecherche && purchaseBlockedOnDossier ? (
                    <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                      Vous avez déjà une commande en cours ou payée pour ce dossier. Suivez-la dans{" "}
                      <Link to="/dashboard?tab=buys" className="font-semibold text-[#2D7FF9] underline">
                        Tableau de bord → Mes achats
                      </Link>
                      .
                    </div>
                  ) : null}
                  {/* Achat au prix affiché : uniquement sans négociation ouverte (sinon le bloc Négociation porte le paiement). */}
                  {!isRecherche && !negotiation && buyerTxsReady && !purchaseBlockedOnDossier && (
                    <Button
                      className="btn-primary w-full h-12 rounded-md text-base font-medium mb-3"
                      onClick={() => setPrePaymentExplainerOpen(true)}
                      disabled={buyLoading || !buyerTxsReady}
                      data-testid="buy-button"
                    >
                      {buyLoading ? "Ouverture..." : "Acheter ce dossier"}
                    </Button>
                  )}
                  {!isRecherche && !negotiation && user && !buyerTxsReady ? (
                    <Button className="btn-primary mb-3 w-full h-12 opacity-70" disabled type="button">
                      Vérification de votre commande…
                    </Button>
                  ) : null}
                  {!isRecherche && !user && (
                    <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded p-3 mb-3 flex items-center justify-between gap-2" data-testid="auth-required-box">
                      <span>Pour acheter, inscrivez-vous ou connectez-vous.</span>
                      <div className="flex gap-1.5">
                        <Link to="/login" state={resumeAfterAuth} className="text-[#2D7FF9] font-semibold hover:underline" data-testid="goto-login-inline">Connexion</Link>
                        <span className="text-slate-300">·</span>
                        <Link to="/register" state={resumeAfterAuth} className="text-[#2D7FF9] font-semibold hover:underline" data-testid="goto-register-inline">Inscription</Link>
                      </div>
                    </div>
                  )}
                  {isRecherche && !user && (
                    <div className="text-xs text-slate-700 bg-slate-50 border border-slate-200 rounded p-3 mb-3 flex items-center justify-between gap-2">
                      <span>Inscription ou connexion requise</span>
                      <div className="flex gap-1.5">
                        <Link to="/login" state={resumeAfterAuth} className="text-[#2D7FF9] font-semibold hover:underline" data-testid="goto-login-recherche">Connexion</Link>
                        <span className="text-slate-300">·</span>
                        <Link to="/register" state={resumeAfterAuth} className="text-[#2D7FF9] font-semibold hover:underline" data-testid="goto-register-recherche">Inscription</Link>
                      </div>
                    </div>
                  )}
                  {!negotiation && (
                    <>
                      {!isRecherche && (
                        <Button variant="outline" className="w-full" onClick={() => (user ? startNegotiation() : navigate("/register", { state: resumeAfterAuth }))} data-testid="start-negotiation">
                          <Handshake className="w-4 h-4 mr-2" />Proposer un prix
                        </Button>
                      )}
                      {isRecherche && (
                        <Button
                          className="w-full btn-primary"
                          onClick={() => {
                            if (!user) return navigate("/register", { state: resumeAfterAuth });
                            setProposeRdvOpen(true);
                          }}
                          data-testid="propose-rdv-recherche"
                        >
                          <CalendarClock className="w-4 h-4 mr-2" />
                          Proposer
                        </Button>
                      )}
                    </>
                  )}
                </>
              )}

              {isSeller && (
                <div className="space-y-3">
                  <div className={cn("text-sm text-slate-600 bg-slate-50 rounded-lg p-3 leading-snug", compactEmbed && isRecherche && "text-xs")}>
                    {isRecherche
                      ? compactEmbed
                        ? "Les propositions arrivent si une fiche vente correspond à vos critères (département, qualification…)."
                        : "Vous avez publié cette demande d’achat. Un vendeur ne peut proposer que si sa fiche du même produit respecte les critères d’adéquation que vous avez imposés (ou les critères renseignés, si vous aviez publié en mode classique)."
                      : "Vous êtes le vendeur de ce dossier."}
                  </div>
                  {dossier.status === "online" ? (
                    editMode ? (
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          className="w-full btn-primary"
                          onClick={saveEdits}
                          disabled={savingEdits}
                          data-testid="seller-save-annonce"
                        >
                          {savingEdits ? "Enregistrement…" : "Enregistrer"}
                        </Button>
                        <Button
                          variant="outline"
                          className="w-full"
                          onClick={cancelEdit}
                          disabled={savingEdits}
                          data-testid="seller-cancel-annonce"
                        >
                          Annuler
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={openEdit}
                        data-testid="seller-edit-annonce"
                      >
                        Modifier l’annonce
                      </Button>
                    )
                  ) : null}
                  {!compactEmbed ? (
                    <p className="text-xs text-slate-500">
                      Cette page est une <strong className="font-medium text-slate-600">vue publique</strong> de l’annonce. Pour
                      changer prix, texte ou critères, ouvrez{" "}
                      <Link to="/dashboard?tab=annonces" className="font-semibold text-[#2D7FF9] underline">
                        Tableau de bord → Mes annonces
                      </Link>{" "}
                      puis « Modifier » sur la ligne concernée.
                    </p>
                  ) : null}
                </div>
              )}
            </div>

            {negotiation && (
              <div className="bg-white border border-[#2D7FF9]/20 rounded-lg p-6 mb-4" data-testid="negotiation-panel">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-display font-bold">Négociation</h3>
                  <Badge className={negotiation.status === "accepted" ? "bg-emerald-100 text-emerald-800" : negotiation.status === "open" ? "bg-blue-100 text-blue-800" : "bg-slate-200 text-slate-700"}>
                    {negotiationStatusLabelFr(negotiation.status)}
                  </Badge>
                </div>

                {negotiation.status === "accepted" ? (
                  <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Prix public</span>
                      <span className="font-display font-black text-[#0B0D12]">{Number(publicPrice).toLocaleString("fr-FR")}€</span>
                    </div>
                    <div className="mt-2 flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Prix négocié (final)</span>
                      <span className="font-display font-black text-[#2D7FF9]">{Number(negotiatedFinalPrice ?? publicPrice).toLocaleString("fr-FR")}€</span>
                    </div>
                  </div>
                ) : null}

                <div className="space-y-2 max-h-64 overflow-y-auto mb-4">
                  {(negotiation.offers || []).length === 0 && (
                    <div className="text-xs text-slate-500 text-center py-4">Aucune offre. Faites la première proposition ci-dessous.</div>
                  )}
                  {(negotiation.offers || []).map((o) => (
                    <div
                      key={o.offer_id || o.id || `${o.from_user_id || "u"}_${o.created_at || "t"}_${o.amount || "a"}`}
                      className={`p-3 rounded-md border ${o.from_user_id === user?.user_id ? "bg-[#2D7FF9]/5 border-[#2D7FF9]/20" : "bg-slate-50 border-slate-200"}`}
                    >
                      <div className="flex justify-between items-center text-xs text-slate-500 mb-1">
                        <span>{o.from_user_id === user?.user_id ? "Vous" : isSeller ? "Acheteur" : "Vendeur"}</span>
                        {o.status ? (
                          <Badge variant="outline" className="text-[10px]">
                            {negotiationOfferStatusLabelFr(o.status)}
                          </Badge>
                        ) : null}
                      </div>
                      <div className="font-display font-bold text-lg text-[#2D7FF9]">{Number(o.amount).toLocaleString("fr-FR")}€</div>
                      {o.message && <div className="text-xs text-slate-600 mt-1">{o.message}</div>}
                    </div>
                  ))}
                </div>

                {negotiation.status === "open" && (
                  <>
                    {canRespondToLast && (
                      <div className="grid grid-cols-3 gap-2 mb-3">
                        <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => respond("accept")} data-testid="accept-offer">
                          <Check className="w-4 h-4 mr-1" /> Accepter
                        </Button>
                        <Button className="bg-red-600 hover:bg-red-700 text-white" onClick={() => respond("reject")} data-testid="reject-offer">
                          <X className="w-4 h-4 mr-1" /> Refuser
                        </Button>
                        <Button
                          variant="outline"
                          className="border-[#2D7FF9] text-[#2D7FF9] hover:bg-[#2D7FF9]/5"
                          onClick={() => {
                            if (lastOffer?.amount) setOfferAmount(String(lastOffer.amount));
                            document.getElementById("offer-amount-field")?.focus();
                          }}
                          data-testid="counter-offer-btn"
                        >
                          <Handshake className="w-4 h-4 mr-1" /> Contre
                        </Button>
                      </div>
                    )}
                    <div className="space-y-2">
                      <Input
                        id="offer-amount-field"
                        type="text"
                        inputMode="decimal"
                        placeholder="Montant (€)"
                        value={offerAmount}
                        onChange={(e) => setOfferAmount(e.target.value)}
                        className="font-mono tabular-nums"
                        data-testid="offer-amount-input"
                      />
                      <Button className="btn-primary w-full" onClick={sendOffer} data-testid="send-offer">
                        {canRespondToLast ? "Envoyer la contre-proposition" : "Proposer ce prix"}
                      </Button>
                    </div>
                  </>
                )}

                {negotiation.status === "accepted" && !isSeller && !isRecherche && buyerTxsReady && purchaseBlockedOnDossier ? (
                  <p className="text-sm text-amber-800 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2">
                    Vous avez déjà passé la commande pour ce RDV — voir{" "}
                    <Link to="/dashboard?tab=buys" className="font-semibold text-[#2D7FF9] underline">
                      Mes achats
                    </Link>
                    .
                  </p>
                ) : null}
                {negotiation.status === "accepted" && !isSeller && !isRecherche && buyerTxsReady && !purchaseBlockedOnDossier ? (
                  <Button
                    className="btn-primary w-full"
                    onClick={() => setPrePaymentExplainerOpen(true)}
                    disabled={buyLoading || !buyerTxsReady}
                    data-testid="buy-negotiated-button"
                  >
                    {buyLoading ? "Ouverture..." : `Acheter ${Number(buyPrice).toLocaleString("fr-FR")}€`}
                  </Button>
                ) : null}
                {negotiation.status === "accepted" && !isSeller && !isRecherche && user && !buyerTxsReady ? (
                  <Button className="btn-primary w-full opacity-70" disabled type="button">
                    Vérification de votre commande…
                  </Button>
                ) : null}
                {negotiation.status === "accepted" && !isSeller && isRecherche && (
                  <p className="text-xs text-slate-600">
                    Négociation acceptée. Vous pouvez maintenant confirmer le rendez-vous via le parcours prévu ; l’achat en un clic de la marketplace s’applique aux fiches de vente, pas aux demandes d’achat.
                  </p>
                )}
              </div>
            )}

          </aside>
        </div>
      </div>

      <Dialog
        open={proposeRdvOpen}
        onOpenChange={(o) => {
          setProposeRdvOpen(o);
          if (!o) {
            setProposeVenteSelected(new Set());
          }
        }}
      >
        <DialogContent className="max-w-md" data-testid="propose-rdv-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Proposer des fiches</DialogTitle>
            <DialogDescription>
              Cochez <strong>une ou plusieurs</strong> de vos annonces de vente qui répondent à cette demande : même produit et mêmes
              valeurs pour chaque critère exigé sur l’annonce « Je cherche ». L’acheteur les verra dans son onglet Suggestions.
            </DialogDescription>
          </DialogHeader>
          {ventesEligibles.length === 0 ? (
            <div className="space-y-3 py-2 text-sm text-slate-700">
              <p>
                Aucune de vos fiches n’est éligible pour le moment : produit identique, statut « en ligne » et champs de qualification
                strictement alignés sur cette annonce.
              </p>
              <p>
                <Link to="/nouveau-dossier" className="text-[#2D7FF9] font-semibold hover:underline" data-testid="link-nouveau-dossier-vente">
                  Publier une fiche de vente
                </Link>{" "}
                (type de produit et mêmes critères que l’achat) puis revenez sur cette page.
              </p>
            </div>
          ) : (
            <div className="max-h-[min(50vh,320px)] space-y-2 overflow-y-auto rounded-lg border border-slate-200 bg-slate-50/60 py-2 pr-1">
              {ventesEligibles.map((d) => {
                const did = String(d.dossier_id ?? d.id ?? "");
                const checked = proposeVenteSelected.has(did);
                return (
                  <label
                    key={did}
                    className="flex cursor-pointer items-start gap-3 rounded-md px-3 py-2.5 hover:bg-white"
                    htmlFor={`propose-vente-${did}`}
                  >
                    <Checkbox
                      id={`propose-vente-${did}`}
                      checked={checked}
                      onCheckedChange={() => toggleProposeVente(did)}
                      data-testid={`propose-vente-checkbox-${did}`}
                      className="mt-0.5"
                    />
                    <span className="min-w-0 flex-1 text-sm leading-snug text-slate-800">
                      <span className="font-medium">{d.title || "Sans titre"}</span>
                      <span className="tabular-nums text-slate-600"> — {Number(d.price || 0).toLocaleString("fr-FR")}€</span>
                    </span>
                  </label>
                );
              })}
            </div>
          )}
          <DialogFooter className="gap-2 sm:justify-end">
            <Button variant="outline" type="button" onClick={() => setProposeRdvOpen(false)}>
              Annuler
            </Button>
            {ventesEligibles.length > 0 && (
              <Button
                className="btn-primary"
                type="button"
                disabled={proposeVenteSelected.size === 0 || proposeSubmitting}
                onClick={() => void sendRechercheProposals()}
                data-testid="confirm-propose-rdv"
              >
                {proposeSubmitting ? "Envoi…" : "Envoyer les propositions"}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={prePaymentExplainerOpen} onOpenChange={setPrePaymentExplainerOpen}>
        <DialogContent
          className="flex max-h-[min(90vh,760px)] w-[calc(100vw-1.5rem)] max-w-xl flex-col gap-0 overflow-hidden p-0 shadow-lg sm:rounded-2xl"
          data-testid="pre-payment-explainer"
        >
          <DialogHeader className="border-b border-slate-100 bg-white px-6 pb-4 pt-5 sm:px-8 sm:pb-5 sm:pt-6">
            <div className="mb-2 flex items-start gap-3">
              <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-[#2D7FF9]">
                <Lock className="h-5 w-5" aria-hidden />
              </span>
              <div className="min-w-0 space-y-2">
                <DialogTitle className="font-display text-left text-lg font-semibold leading-snug text-slate-900 sm:text-xl">
                  Avant de valider le paiement
                </DialogTitle>
                <DialogDescription className="text-left text-sm leading-relaxed text-slate-600">
                  Votre paiement passe par <strong className="font-semibold text-slate-800">Stripe</strong> et reste sous{" "}
                  <strong className="font-semibold text-slate-800">séquestre</strong> jusqu’à confirmation du RDV. Lisez tout le
                  texte ci-dessous <strong className="font-semibold text-slate-800">jusqu’en bas</strong>, puis le bouton vers le
                  paiement s’active — vous pourrez saisir votre carte ou confirmer celle déjà enregistrée.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>
          <div
            ref={prePaymentScrollRef}
            className="min-h-0 flex-1 overflow-y-auto overscroll-contain bg-slate-50/40 px-6 py-5 sm:px-8"
            onScroll={(e) => updatePrePaymentScrollState(e.currentTarget)}
          >
            <EscrowPaymentExplainer />
            <BuyPaymentMethodPicker
              className="mt-5"
              value={buyPaymentMethodId}
              onChange={setBuyPaymentMethodId}
            />
          </div>
          <DialogFooter className="flex-col gap-3 border-t border-slate-100 bg-white px-6 py-4 sm:px-8 sm:py-5">
            {!prePaymentScrollDone && !buyLoading ? (
              <p className="w-full text-center text-xs text-slate-600" role="status">
                Faites défiler jusqu’au bas du cadre pour débloquer « Continuer vers le paiement ».
              </p>
            ) : null}
            <div className="flex w-full flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <Button
                type="button"
                variant="outline"
                className="h-11 w-full rounded-lg border-slate-200 sm:w-auto"
                onClick={() => setPrePaymentExplainerOpen(false)}
              >
                Annuler
              </Button>
              <Button
                type="button"
                className="btn-primary h-11 w-full rounded-lg px-6 font-semibold sm:w-auto disabled:opacity-60"
                onClick={continueAfterPaymentExplainer}
                disabled={buyLoading || !prePaymentScrollDone}
                title={!prePaymentScrollDone && !buyLoading ? "Lisez tout le contenu jusqu’en bas pour continuer" : undefined}
                data-testid="pre-payment-continue"
              >
                {buyLoading ? "Ouverture…" : "Continuer vers le paiement"}
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <StripePaymentDialog
        open={paymentDialog.open}
        onOpenChange={(o) =>
          setPaymentDialog((p) => {
            const nextOpen = Boolean(o);
            if (nextOpen) return { ...(p || {}), open: true };
            return { open: false, clientSecret: "", txId: "", initialPaymentMethods: null };
          })
        }
        clientSecret={paymentDialog.clientSecret}
        initialPaymentMethods={paymentDialog.initialPaymentMethods}
        onClientSecretChange={(cs) =>
          setPaymentDialog((p) => ({ ...(p || {}), clientSecret: String(cs || "") }))
        }
        transactionId={paymentDialog.txId}
        returnUrl={dossierReturnUrl}
        showEscrowInfo={false}
        title="Paiement du dossier"
        subtitle="En séquestre pendant l’appel · débit après confirmation du RDV."
        onSuccess={async () => {
          await load();
          await loadNegotiation();
          navigate("/dashboard?tab=buys");
          toast.success("Paiement enregistré. Retrouvez ce RDV dans « Mes achats » pour appeler.");
        }}
      />

      <StripePaymentDialog
        open={subscriptionPay.open}
        onOpenChange={(o) =>
          setSubscriptionPay({
            open: Boolean(o),
            clientSecret: Boolean(o) ? subscriptionPay.clientSecret : "",
            intentType: Boolean(o) ? subscriptionPay.intentType : "payment",
            amountDue: Boolean(o) ? subscriptionPay.amountDue : null,
            currency: Boolean(o) ? subscriptionPay.currency : "eur",
            plan: Boolean(o) ? subscriptionPay.plan : "monthly",
            planAmount: Boolean(o) ? subscriptionPay.planAmount : null,
            planCurrency: Boolean(o) ? subscriptionPay.planCurrency : "eur",
            nextAction: Boolean(o) ? subscriptionPay.nextAction : null,
            paymentIntentId: Boolean(o) ? subscriptionPay.paymentIntentId : "",
          })
        }
        clientSecret={subscriptionPay.clientSecret}
        intentType={subscriptionPay.intentType}
        returnUrl={dossierReturnUrl}
        title="Abonnement"
        subtitle={
          <div className="space-y-1">
            <div className="text-sm font-semibold text-slate-900">
              Abonnement :{" "}
              {subscriptionPay.planAmount != null
                ? `${(Number(subscriptionPay.planAmount) / 100).toLocaleString("fr-FR")}€/${subscriptionPay.plan === "yearly" ? "an" : "mois"}`
                : "—"}
            </div>
            <div className="text-sm font-semibold text-slate-900">
              Débit immédiat aujourd’hui :{" "}
              {subscriptionPay.amountDue != null
                ? `${(Number(subscriptionPay.amountDue) / 100).toLocaleString("fr-FR")}€`
                : "—"}
            </div>
            <div className="text-[11px] text-slate-600">
              Renouvellement automatique. Vous pourrez changer de carte à tout moment.
            </div>
          </div>
        }
        showEscrowInfo={false}
        confirmLabel="S’abonner"
        onSuccess={async (intent) => {
          const planSnap = subscriptionPay.plan;
          const nextSnap = subscriptionPay.nextAction;
          const closeSubModal = () => {
            setSubscriptionPay({
              open: false,
              clientSecret: "",
              intentType: "payment",
              amountDue: null,
              currency: "eur",
              plan: "monthly",
              planAmount: null,
              planCurrency: "eur",
              nextAction: null,
              paymentIntentId: "",
            });
            setSubscribing(false);
          };
          try {
            if (nextSnap === "activate_subscription") {
              const piId = intent?.id || subscriptionPay.paymentIntentId;
              if (!piId) throw new Error("PaymentIntent manquant");
              await api.post("/subscriptions/activate", { plan: planSnap, payment_intent_id: String(piId) });
              const activeOk = await waitForActiveSubscription(api);
              closeSubModal();
              if (!activeOk) {
                setSubscriptionSyncDialog({
                  open: true,
                  plan: planSnap,
                  paymentIntentId: String(piId),
                  syncing: false,
                });
                toast.info("Paiement reçu — finalisation de l’abonnement…");
                return;
              }
            }
          } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Impossible de finaliser l’abonnement");
            setSubscribing(false);
            return;
          }
          closeSubModal();
          toast.success("Abonnement en cours d’activation");
          void resumePendingBuy();
        }}
      />

      <AlertDialog
        open={subscriptionSyncDialog.open}
        onOpenChange={(o) => {
          if (!o) setSubscriptionSyncDialog((prev) => ({ ...prev, open: false }));
        }}
      >
        <AlertDialogContent className="rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle>Finalisation de l’abonnement</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-600">
              Votre paiement est bien enregistré. La mise à jour du compte peut prendre quelques secondes — vous pouvez relancer la synchronisation sans payer à nouveau.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-xl">Fermer</AlertDialogCancel>
            <Button
              type="button"
              className="rounded-xl"
              onClick={() => void handleSubscriptionSyncRetry()}
              disabled={subscriptionSyncDialog.syncing}
            >
              {subscriptionSyncDialog.syncing ? "Synchronisation…" : "Réessayer"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <SubscriptionRequiredPurchaseModal
        open={subscribeDialog.open}
        onOpenChange={(o) => {
          if (!o) setSubscribeDialog({ open: false, plan: "monthly", dossierId: "", negId: "" });
        }}
        subscribing={subscribing}
        onSelectPlan={(plan) => void startSubscribe(plan)}
      />
    </div>
  );
};

export default DossierDetail;
